import { useSyncExternalStore } from 'react'

import { kv } from '@/shared/platform/kv'

/**
 * Личные настройки чата: выключить его совсем и заткнуть соперника.
 *
 * Всё в хранилище устройства (фасад `kv`), а не на сервере, и по той же
 * причине, что тема и вид поля: это настройка вида, а не часть партии.
 * Серверных жалоб мы не заводим — пока стол закрытый, за ним договорятся
 * сами, а за открытым и сказать-то можно только готовой фразой
 * (docs/CHAT.md, «Экран»).
 *
 * Мут — по МЕСТУ за столом и внутри партии: другого устойчивого имени у
 * соперника нет. Гость может назваться как угодно и в другой партии
 * назваться иначе, а место в партии уникально — им же пользуются реплики
 * и удаление бота.
 */
const OFF_KEY = 'ludo.chat'
const MUTE_KEY = 'ludo.chat.mute'

type Muted = Record<string, number[]>

const listeners = new Set<() => void>()

function announce(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

function readMuted(): Muted {
  try {
    const raw = kv.get(MUTE_KEY)

    return raw === null ? {} : (JSON.parse(raw) as Muted)
  } catch {
    // Битая запись не должна ронять экран: без настройки чат просто
    // будет слышно целиком.
    return {}
  }
}

function readOn(): boolean {
  return kv.get(OFF_KEY) !== 'off'
}

let on = readOn()
let muted = readMuted()

/** Показывать ли чат вообще. */
export function useChatOn(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => on,
    () => on,
  )
}

export function setChatOn(next: boolean): void {
  if (next === on) return

  on = next
  kv.set(OFF_KEY, next ? 'on' : 'off')
  announce()
}

/**
 * Кого не слышно за этим столом.
 *
 * Пустой массив — общий на все партии без мута: useSyncExternalStore
 * сравнивает снимок по ссылке, и новый литерал на каждом вызове давал бы
 * бесконечный рендер.
 */
const NOBODY: number[] = []

export function useMutedSeats(gameId: string): number[] {
  return useSyncExternalStore(
    subscribe,
    () => muted[gameId] ?? NOBODY,
    () => muted[gameId] ?? NOBODY,
  )
}

export function toggleMuted(gameId: string, seat: number): void {
  const before = muted[gameId] ?? []
  const after = before.includes(seat)
    ? before.filter((one) => one !== seat)
    : [...before, seat].sort((a, b) => a - b)

  muted = { ...muted, [gameId]: after }
  kv.set(MUTE_KEY, JSON.stringify(muted))
  announce()
}
